import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Shield, 
  Type, 
  Download, 
  Lock, 
  Unlock, 
  Settings, 
  Moon, 
  Sun, 
  Maximize, 
  Minimize, 
  BarChart3,
  Key,
  FileText,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  Trash2,
  Copy,
  RefreshCw,
  Clock,
  Zap
} from 'lucide-react';

interface AnalyticsData {
  operationsPerformed: number;
  totalTextLength: number;
  successRate: number;
  averageTime: number;
}

interface SettingsData {
  encoding: string;
  keyFormat: string;
  keyLength: string;
  dropBytes: number;
  autoGenerate: boolean;
  showTimestamp: boolean;
}

interface ResultData {
  content: string;
  operation: string;
  timestamp: string;
  size: number;
}

const RC4EncryptDecrypt = () => {
  const [isDark, setIsDark] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [inputText, setInputText] = useState<string>('');
  const [key, setKey] = useState<string>('');
  const [showKey, setShowKey] = useState<boolean>(false);
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  const [processing, setProcessing] = useState<boolean>(false);
  const [result, setResult] = useState<ResultData | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    operationsPerformed: 0,
    totalTextLength: 0,
    successRate: 100,
    averageTime: 0
  });
  const [settings, setSettings] = useState<SettingsData>({
    encoding: 'Base64',
    keyFormat: 'Hex',
    keyLength: '128',
    dropBytes: 0,
    autoGenerate: true,
    showTimestamp: true
  });

  const containerRef = useRef<HTMLDivElement>(null);

  // Unicode-safe base64 encoding/decoding
  const encodeUnicode = (str: string): string => {
    return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
      return String.fromCharCode(parseInt(p1, 16));
    }));
  };

  const decodeUnicode = (str: string): string => {
    return decodeURIComponent(atob(str).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  };

  // Generate random RC4 key
  const generateKey = useCallback((): string => {
    const keyLength = parseInt(settings.keyLength) / 8; // Convert bits to bytes
    const bytes = new Uint8Array(keyLength);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('').toUpperCase();
  }, [settings.keyLength]);

  // Analytics tracking
  const trackOperation = useCallback((type: string, success: boolean, textLength: number, duration: number) => {
    setAnalytics(prev => ({
      operationsPerformed: prev.operationsPerformed + 1,
      totalTextLength: prev.totalTextLength + textLength,
      successRate: ((prev.successRate * prev.operationsPerformed + (success ? 100 : 0)) / (prev.operationsPerformed + 1)),
      averageTime: ((prev.averageTime * prev.operationsPerformed + duration) / (prev.operationsPerformed + 1))
    }));
  }, []);

  // RC4 Key Scheduling Algorithm (KSA)
  const initializeRC4 = (key: string): number[] => {
    const S: number[] = [];
    const keyBytes: number[] = [];
    
    // Convert hex key to bytes
    for (let i = 0; i < key.length; i += 2) {
      keyBytes.push(parseInt(key.substr(i, 2), 16));
    }
    
    // Initialize S array
    for (let i = 0; i < 256; i++) {
      S[i] = i;
    }
    
    // Key-scheduling algorithm
    let j = 0;
    for (let i = 0; i < 256; i++) {
      j = (j + S[i] + keyBytes[i % keyBytes.length]) % 256;
      [S[i], S[j]] = [S[j], S[i]]; // Swap
    }
    
    return S;
  };

  // RC4 Pseudo-Random Generation Algorithm (PRGA)
  const generateKeystream = (S: number[], length: number, dropBytes: number = 0): number[] => {
    const keystream: number[] = [];
    let i = 0, j = 0;
    
    // Drop initial bytes (security improvement)
    for (let drop = 0; drop < dropBytes; drop++) {
      i = (i + 1) % 256;
      j = (j + S[i]) % 256;
      [S[i], S[j]] = [S[j], S[i]];
    }
    
    // Generate keystream
    for (let k = 0; k < length; k++) {
      i = (i + 1) % 256;
      j = (j + S[i]) % 256;
      [S[i], S[j]] = [S[j], S[i]]; // Swap
      keystream.push(S[(S[i] + S[j]) % 256]);
    }
    
    return keystream;
  };

  // RC4 encryption/decryption
  const performRC4 = useCallback(async () => {
    if (!inputText.trim() || !key.trim()) return;

    setProcessing(true);
    const startTime = Date.now();

    try {
      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 300 + Math.random() * 1000));

      let result: string;
      const keyBytes = key.replace(/\s/g, '');
      const expectedKeyLength = parseInt(settings.keyLength) / 4; // Hex chars needed
      
      // Validate key format
      if (settings.keyFormat === 'Hex') {
        if (!/^[0-9A-Fa-f]*$/.test(keyBytes)) {
          throw new Error('Invalid hex key format. Only hexadecimal characters allowed.');
        }
        if (keyBytes.length < 2 || keyBytes.length > 512) {
          throw new Error('Key length must be between 1-256 bytes (2-512 hex characters).');
        }
      }

      if (operation === 'encrypt') {
        // RC4 Encryption
        const S = initializeRC4(keyBytes);
        const keystream = generateKeystream([...S], inputText.length, settings.dropBytes);
        
        const encrypted = Array.from(inputText)
          .map((char, i) => String.fromCharCode(char.charCodeAt(0) ^ keystream[i]))
          .join('');

        // Apply encoding
        if (settings.encoding === 'Base64') {
          result = encodeUnicode(encrypted);
        } else if (settings.encoding === 'Hex') {
          result = Array.from(encrypted)
            .map(char => char.charCodeAt(0).toString(16).padStart(2, '0'))
            .join('').toUpperCase();
        } else {
          result = encrypted;
        }
      } else {
        // RC4 Decryption
        let encryptedText = inputText;
        
        try {
          // Decode based on encoding
          if (settings.encoding === 'Base64') {
            encryptedText = decodeUnicode(inputText);
          } else if (settings.encoding === 'Hex') {
            if (!/^[0-9A-Fa-f]*$/.test(inputText.replace(/\s/g, ''))) {
              throw new Error('Invalid hex format for decryption');
            }
            const hexText = inputText.replace(/\s/g, '');
            if (hexText.length % 2 !== 0) {
              throw new Error('Invalid hex length. Must be even number of characters.');
            }
            encryptedText = '';
            for (let i = 0; i < hexText.length; i += 2) {
              encryptedText += String.fromCharCode(parseInt(hexText.substr(i, 2), 16));
            }
          }

          // RC4 Decryption (same as encryption - XOR is symmetric)
          const S = initializeRC4(keyBytes);
          const keystream = generateKeystream([...S], encryptedText.length, settings.dropBytes);
          
          result = Array.from(encryptedText)
            .map((char, i) => String.fromCharCode(char.charCodeAt(0) ^ keystream[i]))
            .join('');
            
        } catch {
          throw new Error('Invalid encrypted text or wrong key');
        }
      }

      const duration = Date.now() - startTime;
      const timestamp = new Date().toLocaleString();
      
      setResult({
        content: result,
        operation: operation,
        timestamp: timestamp,
        size: result.length
      });

      trackOperation(operation, true, inputText.length, duration);

    } catch (error) {
      const duration = Date.now() - startTime;
      trackOperation(operation, false, inputText.length, duration);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Error: ${errorMessage}`);
    } finally {
      setProcessing(false);
    }
  }, [inputText, key, operation, settings, trackOperation, encodeUnicode, decodeUnicode]);

  // Fullscreen handling
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Auto-generate key on mount if enabled
  useEffect(() => {
    if (settings.autoGenerate && !key) {
      setKey(generateKey());
    }
  }, [settings.autoGenerate, key, generateKey]);

  // Regenerate key when key length changes
  useEffect(() => {
    if (settings.autoGenerate && key) {
      setKey(generateKey());
    }
  }, [settings.keyLength]);

  const copyToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const downloadResult = (content: string, filename: string): void => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearInputs = (): void => {
    setInputText('');
    if (!settings.autoGenerate) {
      setKey('');
    }
    setResult(null);
  };

  const getExpectedKeyLength = (): number => {
    return parseInt(settings.keyLength) / 4; // Convert bits to hex characters
  };

  return (
    <>
      <Helmet>
        <title>RC4 Encrypt & Decrypt - Stream Cipher Encryption Tool</title>
        <meta name="description" content="Professional RC4 stream cipher encryption and decryption tool with variable key lengths, drop bytes, and multiple encoding formats." />
        <meta name="keywords" content="RC4 encryption, RC4 decryption, stream cipher, variable key, cryptography, Rivest Cipher 4" />
        <meta name="author" content="Stream Cipher Tools" />
        <meta property="og:title" content="RC4 Encrypt & Decrypt Tool" />
        <meta property="og:description" content="Secure text encryption and decryption using the RC4 stream cipher algorithm." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://example.com/rc4-encrypt-decrypt" />
      </Helmet>

      <div 
        ref={containerRef}
        className={`min-h-screen transition-all duration-300 ${
          isDark ? 'bg-gray-900 text-white' : 'bg-gradient-to-br from-orange-50 to-amber-100 text-gray-900'
        } ${isFullscreen ? 'p-0' : 'p-4'}`}
      >
        {/* Header */}
        <header className="flex items-center justify-between mb-8 p-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20">
          <div className="flex items-center space-x-3">
            <Zap className="w-8 h-8 text-orange-500" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
              RC4 Encrypt & Decrypt
            </h1>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700' : 'hover:bg-white/20'
              } ${showAnalytics ? 'bg-orange-500 text-white' : ''}`}
            >
              <BarChart3 className="w-5 h-5" />
            </button>
            
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700' : 'hover:bg-white/20'
              } ${showSettings ? 'bg-orange-500 text-white' : ''}`}
            >
              <Settings className="w-5 h-5" />
            </button>
            
            <button
              onClick={() => setIsDark(!isDark)}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700' : 'hover:bg-white/20'
              }`}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            <button
              onClick={toggleFullscreen}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700' : 'hover:bg-white/20'
              }`}
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Panel */}
          <div className="lg:col-span-2">
            <div className={`p-6 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
            } backdrop-blur-sm border`}>
              
              {/* Operation Toggle */}
              <div className="flex mb-6 p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <button
                  onClick={() => setOperation('encrypt')}
                  className={`flex-1 flex items-center justify-center py-2 px-4 rounded-md transition-colors ${
                    operation === 'encrypt'
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Encrypt
                </button>
                <button
                  onClick={() => setOperation('decrypt')}
                  className={`flex-1 flex items-center justify-center py-2 px-4 rounded-md transition-colors ${
                    operation === 'decrypt'
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Unlock className="w-4 h-4 mr-2" />
                  Decrypt
                </button>
              </div>

              {/* Input Text */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">
                    <Type className="w-4 h-4 inline mr-1" />
                    {operation === 'encrypt' ? 'Plain Text' : 'Encrypted Text'}
                  </label>
                  <div className="flex space-x-2">
                    <button
                      onClick={clearInputs}
                      className="text-xs px-2 py-1 rounded bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                    >
                      <Trash2 className="w-3 h-3 inline mr-1" />
                      Clear
                    </button>
                  </div>
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder={operation === 'encrypt' ? 'Enter text to encrypt...' : 'Enter encrypted text to decrypt...'}
                  rows={6}
                  className={`w-full px-4 py-3 rounded-lg border resize-none ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:ring-2 focus:ring-orange-500 focus:border-transparent`}
                />
                <div className="text-xs text-gray-500 mt-1">
                  Characters: {inputText.length}
                </div>
              </div>

              {/* RC4 Key */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">
                    <Key className="w-4 h-4 inline mr-1" />
                    RC4 Key (Variable Length)
                  </label>
                  <button
                    onClick={() => setKey(generateKey())}
                    className="text-xs px-3 py-1 rounded bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-800 transition-colors"
                  >
                    <RefreshCw className="w-3 h-3 inline mr-1" />
                    Generate
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showKey ? 'text' : 'password'}
                    value={key}
                    onChange={(e) => setKey(e.target.value.toUpperCase())}
                    placeholder="Enter hex key (1-256 bytes)"
                    className={`w-full px-4 py-3 pr-12 rounded-lg border font-mono ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-orange-500 focus:border-transparent`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey(!showKey)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="text-xs text-gray-500 mt-1 flex justify-between">
                  <span>Key length: {key.length} hex chars ({Math.floor(key.length / 2)} bytes)</span>
                  <span className={`${key.length >= 2 && key.length <= 512 ? 'text-green-500' : 'text-orange-500'}`}>
                    {key.length >= 2 && key.length <= 512 ? '✓ Valid' : '⚠ 1-256 bytes required'}
                  </span>
                </div>
              </div>

              {/* Process Button */}
              <button
                onClick={performRC4}
                disabled={!inputText.trim() || !key.trim() || key.length < 2 || processing}
                className={`w-full py-4 px-6 rounded-lg font-medium transition-all transform ${
                  !inputText.trim() || !key.trim() || key.length < 2 || processing
                    ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-orange-500 to-amber-600 text-white hover:from-orange-600 hover:to-amber-700 hover:scale-105 shadow-lg'
                }`}
              >
                {processing ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Processing RC4...
                  </div>
                ) : (
                  `${operation === 'encrypt' ? 'Encrypt' : 'Decrypt'} with RC4`
                )}
              </button>

              {/* Result */}
              {result && (
                <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                      <span className="font-medium text-green-700 dark:text-green-300">
                        {result.operation === 'encrypt' ? 'Text Encrypted with RC4!' : 'Text Decrypted with RC4!'}
                      </span>
                    </div>
                    {settings.showTimestamp && (
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="w-3 h-3 mr-1" />
                        {result.timestamp}
                      </div>
                    )}
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 mb-3">
                    <pre className="whitespace-pre-wrap break-all text-sm font-mono">
                      {result.content}
                    </pre>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-300">
                      Length: {result.size} characters
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => copyToClipboard(result.content)}
                        className="flex items-center px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy
                      </button>
                      <button
                        onClick={() => downloadResult(result.content, `rc4_${result.operation}_result.txt`)}
                        className="flex items-center px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Download
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Side Panel */}
          <div className="space-y-6">
            {/* Analytics Panel */}
            {showAnalytics && (
              <div className={`p-4 rounded-xl ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
              } backdrop-blur-sm border`}>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2" />
                  Analytics
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Operations</span>
                    <span className="font-medium">{analytics.operationsPerformed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Text Processed</span>
                    <span className="font-medium">{analytics.totalTextLength} chars</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Success Rate</span>
                    <span className="font-medium text-green-500">{analytics.successRate.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Avg. Time</span>
                    <span className="font-medium">{(analytics.averageTime / 1000).toFixed(1)}s</span>
                  </div>
                </div>
              </div>
            )}

            {/* Settings Panel */}
            {showSettings && (
              <div className={`p-4 rounded-xl ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
              } backdrop-blur-sm border`}>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Settings className="w-5 h-5 mr-2" />
                  RC4 Settings
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Key Length (bits)</label>
                    <select
                    // min={0}
                    //   max="1024"
                    //   step="1"
                      value={settings.keyLength}
                      onChange={(e) => setSettings(prev => ({ ...prev, keyLength: e.target.value }))}
                      className={`w-full px-3 py-2 rounded border ${
                        isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      }`}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Drop initial keystream bytes (security improvement)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.autoGenerate}
                        onChange={(e) => setSettings(prev => ({ ...prev, autoGenerate: e.target.checked }))}
                        className="mr-2"
                      />
                      <span className="text-sm">Auto-generate keys</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.showTimestamp}
                        onChange={(e) => setSettings(prev => ({ ...prev, showTimestamp: e.target.checked }))}
                        className="mr-2"
                      />
                      <span className="text-sm">Show timestamps</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Security Info */}
            <div className={`p-4 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
            } backdrop-blur-sm border`}>
              <h3 className="text-lg font-semibold mb-3 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-orange-500" />
                RC4 Information
              </h3>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <p>• Stream cipher (not block cipher)</p>
                <p>• Variable key length: 1-256 bytes</p>
                <p>• Fast and simple algorithm</p>
                <p>• Uses Key Scheduling Algorithm (KSA)</p>
                <p>• Generates pseudo-random keystream</p>
                <p>• <strong>Warning:</strong> RC4 has known vulnerabilities</p>
              </div>
            </div>

            {/* Algorithm Details */}
            <div className={`p-4 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
            } backdrop-blur-sm border`}>
              <h3 className="text-lg font-semibold mb-3 flex items-center">
                <Zap className="w-5 h-5 mr-2 text-orange-500" />
                Algorithm Steps
              </h3>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <p>• <strong>KSA:</strong> Initialize S-box with key</p>
                <p>• <strong>PRGA:</strong> Generate keystream bytes</p>
                <p>• <strong>XOR:</strong> Combine with plaintext</p>
                <p>• <strong>Drop Bytes:</strong> Skip initial output</p>
                <p>• <strong>Symmetric:</strong> Same process encrypts/decrypts</p>
                <p>• <strong>Speed:</strong> Very fast in software</p>
              </div>
            </div>

            {/* Security Warnings */}
            <div className={`p-4 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
            } backdrop-blur-sm border`}>
              <h3 className="text-lg font-semibold mb-3 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-red-500" />
                Security Warnings
              </h3>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <p>• <strong>Deprecated:</strong> RC4 is no longer secure</p>
                <p>• <strong>Biases:</strong> Statistical weaknesses found</p>
                <p>• <strong>WEP/WPA:</strong> Broken implementations</p>
                <p>• <strong>Use Modern:</strong> AES-GCM, ChaCha20-Poly1305</p>
                <p>• <strong>Educational:</strong> This is for learning only</p>
                <p>• <strong>Production:</strong> Never use RC4 in real systems</p>
              </div>
            </div>

            {/* Performance Notes */}
            <div className={`p-4 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
            } backdrop-blur-sm border`}>
              <h3 className="text-lg font-semibold mb-3 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-orange-500" />
                Historical Context
              </h3>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <p>• <strong>Created:</strong> Ron Rivest, 1987</p>
                <p>• <strong>Usage:</strong> SSL/TLS, WEP, WPA (deprecated)</p>
                <p>• <strong>Advantages:</strong> Simple, fast, variable key</p>
                <p>• <strong>Disadvantages:</strong> Statistical biases</p>
                <p>• <strong>Replacement:</strong> AES in most applications</p>
                <p>• <strong>Legacy:</strong> Still found in old systems</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RC4EncryptDecrypt; 